import { useState } from 'react';
import { get } from 'lodash';
import axios from 'axios';
import qs from 'qs';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import customToast from '../../Components/Toast/Toast';
import UserAPI from '../Api/user';
import PurchaseOrderAPI from '../Api/purchase-order';
import { PurchaseOrder, BulkProdSubStage } from '../Types/purchase-order';
import Api from '../api';
import {
  PurchaseOrderRevision,
  PurchaseOrderRevisionUpdates,
} from '../Types/purchase-order-revision';
import { PurchaseOrderChatMessage } from '../../Components/Chat';

interface MarkTOPShippedType {
  userId: string;
  purchaseOrderId: string;
}

type PurchaseOrderCached = PurchaseOrder & { isLoading: boolean };

export type PoFilters = {
  sub_stages?: string[];
  is_cancelled?: boolean;
  due_date?: { from: string; to: string };
  vendor_revision_status?: string[];
};

export type PaginatedSearchParams = {
  page: number;
  size: number;
  sort?: string;
  filter: PoFilters;
  search?: string;
  withPagination?: boolean;
};

export type Prop65Update = {
  lineId: number;
  prop65: boolean;
};

type SetProp65Params = {
  userId: string;
  poNumber: string;
  updates: Prop65Update[];
};

export const useGetUserPurchaseOrder = (userId: string, id: string) =>
  useQuery(['purchase-order', id], () => UserAPI.getPurchaseOrder(userId, id));

export const usePurchaseOrderByNumber = (poNumber: string) =>
  useQuery(
    ['purchase-order', 'by-number', poNumber],
    () => PurchaseOrderAPI.findOneByPONumber(poNumber, {}),
    {
      enabled: !!poNumber && poNumber.length > 0,
    },
  );

export const useGetUserPurchaseOrderChat = (userId: string, id: string) =>
  useQuery(
    ['purchase-order-chat', id],
    () =>
      UserAPI.getPurchaseOrderChat(userId, id) as Promise<
        Array<PurchaseOrderChatMessage> | undefined
      >,
  );

export const useGetUserPurchaseOrders = (
  userId: string,
  params: PaginatedSearchParams,
) =>
  useQuery<{ purchaseOrders: PurchaseOrder[]; count: number; vendor: any }>(
    ['purchase-orders', userId, params],
    () => {
      const filterStrings: string[] = [];

      if (params.filter.is_cancelled === true && params.filter.sub_stages) {
        filterStrings.push(
          `OR(is_cancelled::eq::${params.filter.is_cancelled}||sub_stage::in::${params.filter.sub_stages?.join(',')})`,
        );
      } else {
        if (typeof params.filter.is_cancelled === 'boolean') {
          filterStrings.push(`is_cancelled::eq::${params.filter.is_cancelled}`);
        }
        if (params.filter.sub_stages) {
          filterStrings.push(
            `sub_stage::in::${params.filter.sub_stages?.join(',')}`,
          );
        }
      }

      if (params.filter.due_date) {
        filterStrings.push(
          `due_date::between::${params.filter.due_date?.from},${params.filter.due_date?.to}`,
        );
      }
      if (params.filter.vendor_revision_status) {
        filterStrings.push(
          `vendor_revision.status::in::${params.filter.vendor_revision_status?.join(',')}`,
        );
      }
      return UserAPI.getPurchaseOrders(
        userId,
        qs.stringify({
          ...params,
          filter:
            filterStrings.length > 0 ? filterStrings.join(';') : undefined,
        }),
      );
    },
    {
      onError: () => {
        customToast(
          {
            title: `An error occurred while getting purchase orders. Please try again later.`,
          },
          { type: 'error' },
        );
      },
    },
  );

interface CountSubStageResponse {
  sub_stage: string;
  count: number;
}

export const useGetPurchaseOrdersSubStageCount = (
  userId: string,
  params: PaginatedSearchParams,
) =>
  useQuery<CountSubStageResponse[]>(
    ['purchase-orders-sub-stage-count', userId, params],
    () =>
      UserAPI.getPurchaseOrdersSubStageCount(
        userId,
        qs.stringify({
          from: params.filter.due_date?.from,
          to: params.filter.due_date?.to,
          filterDateBy: 'due_date',
        }),
      ),
    {
      keepPreviousData: true,
    },
  );

function useUpdatePurchaseOrderLoadingInCache() {
  const queryClient = useQueryClient();

  return (purchaseOrderId: string, updates: Partial<PurchaseOrderCached>) => {
    queryClient.setQueriesData<
      { purchaseOrders: PurchaseOrderCached[] } | undefined
    >({ queryKey: ['purchase-orders'] }, (oldData) => {
      if (!oldData) return oldData;

      if (Array.isArray(oldData.purchaseOrders)) {
        return {
          ...oldData,
          purchaseOrders: oldData.purchaseOrders.map((po: any) =>
            po.po_number === purchaseOrderId ? { ...po, ...updates } : po,
          ),
        };
      }

      return oldData;
    });
  };
}

export const useUpdatePoBulkProductionSubStage = () => {
  const queryClient = useQueryClient();
  const updatePurchaseOrderLoadingInCache =
    useUpdatePurchaseOrderLoadingInCache();

  return useMutation<
    void,
    Error,
    { userId: string; purchaseOrderId: string; sub_stage: BulkProdSubStage },
    { previousPurchaseOrder?: PurchaseOrder }
  >(
    ({ userId, purchaseOrderId, sub_stage }) =>
      UserAPI.updatePoBulkProductionSubStage(
        userId,
        purchaseOrderId,
        sub_stage,
      ),
    {
      onMutate: async ({ purchaseOrderId }) => {
        await queryClient.cancelQueries(['purchase-orders', purchaseOrderId]);

        const previousPurchaseOrder = queryClient.getQueryData<PurchaseOrder>([
          'purchase-orders',
        ]);

        updatePurchaseOrderLoadingInCache(purchaseOrderId, {
          isLoading: true,
        });

        return { previousPurchaseOrder };
      },
      onError: (_error, { purchaseOrderId }, context) => {
        if (context?.previousPurchaseOrder) {
          queryClient.setQueryData(
            ['purchase-order', purchaseOrderId],
            context.previousPurchaseOrder,
          );
        }
      },
      onSettled: async (_data, _error, { purchaseOrderId }) => {
        updatePurchaseOrderLoadingInCache(purchaseOrderId, {
          isLoading: false,
        });
        await queryClient.invalidateQueries(['purchase-orders']);
        await queryClient.invalidateQueries([
          'purchase-order',
          purchaseOrderId,
        ]);
        await queryClient.invalidateQueries([
          'purchase-orders-sub-stage-count',
        ]);
      },
    },
  );
};

export const useAcceptPurchaseOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { userId: string; purchaseOrderId: string },
    { previousPurchaseOrder?: PurchaseOrder }
  >(
    ({ userId, purchaseOrderId }) =>
      UserAPI.acceptPurchaseOrder(userId, purchaseOrderId),
    {
      onMutate: async ({ purchaseOrderId }) => {
        await queryClient.cancelQueries(['purchase-order', purchaseOrderId]);

        const previousPurchaseOrder = queryClient.getQueryData<PurchaseOrder>([
          'purchase-order',
          purchaseOrderId,
        ]);

        queryClient.setQueryData<{ purchaseOrder: PurchaseOrder } | undefined>(
          ['purchase-order', purchaseOrderId],
          (old) =>
            old
              ? {
                  purchaseOrder: {
                    ...old.purchaseOrder,
                    date_of_acceptance: new Date().toISOString(),
                  },
                }
              : old,
        );

        return { previousPurchaseOrder };
      },

      onError: (_error, { purchaseOrderId }, context) => {
        if (context?.previousPurchaseOrder) {
          queryClient.setQueryData(
            ['purchase-order', purchaseOrderId],
            context.previousPurchaseOrder,
          );
        }
      },

      onSettled: async (_data, _error, { purchaseOrderId }) => {
        await queryClient.invalidateQueries([
          'purchase-order',
          purchaseOrderId,
        ]);
      },
    },
  );
};

export const useCreatePurchaseOrderRevision = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      userId,
      purchaseOrderId,
      updates,
    }: {
      userId: string;
      purchaseOrderId: string;
      updates: PurchaseOrderRevisionUpdates;
    }): Promise<{
      po_number: string;
      revision: PurchaseOrderRevision;
      history: PurchaseOrderRevision[];
    }> =>
      UserAPI.createPurchaseOrderRevision(userId, purchaseOrderId, { updates }),
    {
      onSettled: async (_, __, { userId, purchaseOrderId }) => {
        await queryClient.invalidateQueries([
          'purchase-order',
          purchaseOrderId,
        ]);
        await queryClient.invalidateQueries(['purchase-orders', userId]);
      },
    },
  );
};

export const useJoinPoChat = () =>
  useMutation(
    ({
      userId,
      purchaseOrderId,
    }: {
      userId: string;
      purchaseOrderId: string;
    }) => UserAPI.joinPoChat(userId, purchaseOrderId),
  );

export const useUploadInvoice = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadInvoice = async (
    userId: string,
    purchaseOrderId: string,
    formData: { invoice_pdf: File },
  ) => {
    setIsLoading(true);
    setIsSuccess(false);
    setProgress(0);
    const getUrl = `/users/${userId}/purchase-orders/${purchaseOrderId}`;
    const POResponse = await Api.get(getUrl);
    const fileKey = Object.keys(formData)[0];

    if (get(POResponse, `purchaseOrder.${fileKey}.exists`)) {
      const type = 'purchase-orders';
      const name = 'invoice';
      const id = purchaseOrderId;
      const data = { type, name, id };
      const archiveUrl = `/users/${userId}/archive-file`;
      await Api.post(archiveUrl, { data });
    }

    await axios({
      method: 'put',
      url: get(POResponse, `purchaseOrder.${fileKey}`).write,
      data: formData.invoice_pdf,
      headers: {
        'Content-Type': 'application/pdf',
      },
      onUploadProgress: (progressEvent) => {
        setProgress(
          progressEvent.total ? progressEvent.loaded / progressEvent.total : 0,
        );
      },
    })
      .then(async () => {
        toast('File successfully uploaded', { type: 'success' });

        await UserAPI.sendNSInvoice(userId, purchaseOrderId);
        await UserAPI.updatePDFTimestamp(
          userId,
          purchaseOrderId,
          'invoice_timestamp',
        );
      })
      .catch((error) => {
        console.log('File upload error: ', error);
        toast('Something went wrong in file upload', {
          type: 'error',
        });
      })
      .finally(() => {
        setIsLoading(false);
        setProgress(0);
      });

    setIsSuccess(true);
  };

  return { uploadInvoice, isSuccess, progress, isLoading };
};

export const useUploadPackingList = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadPackingList = async (
    userId: string,
    purchaseOrderId: string,
    formData: { packing_list: File },
  ) => {
    setIsLoading(true);
    setIsSuccess(false);
    setProgress(0);
    const getUrl = `/users/${userId}/purchase-orders/${purchaseOrderId}`;
    const POResponse = await Api.get(getUrl);
    const fileKey = Object.keys(formData)[0];

    if (get(POResponse, `purchaseOrder.${fileKey}.exists`)) {
      const type = 'purchase-orders';
      const name = 'packing_list';
      const id = purchaseOrderId;
      const data = { type, name, id };
      const archiveUrl = `/users/${userId}/archive-file`;
      await Api.post(archiveUrl, { data });
    }

    await axios({
      method: 'put',
      url: get(POResponse, `purchaseOrder.${fileKey}`).write,
      data: formData.packing_list,
      headers: {
        'Content-Type': 'application/pdf',
      },
      onUploadProgress: (progressEvent) => {
        setProgress(
          progressEvent.total ? progressEvent.loaded / progressEvent.total : 0,
        );
      },
    })
      .then(async () => {
        toast('File successfully uploaded', { type: 'success' });

        await UserAPI.updatePDFTimestamp(
          userId,
          purchaseOrderId,
          'packing_list_vendor_timestamp',
        );
      })
      .catch((error) => {
        console.log('File upload error: ', error);
        toast('Something went wrong in file upload', {
          type: 'error',
        });
      })
      .finally(() => {
        setIsLoading(false);
        setProgress(0);
      });

    setIsSuccess(true);
  };

  return { uploadPackingList, isSuccess, progress, isLoading };
};

export const useMarkTOPShipped = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    MarkTOPShippedType,
    { previousPurchaseOrder?: PurchaseOrder }
  >(
    ({ userId, purchaseOrderId }) =>
      UserAPI.markTOPShipped(userId, purchaseOrderId),
    {
      onMutate: async ({ purchaseOrderId }) => {
        await queryClient.cancelQueries(['purchase-order', purchaseOrderId]);

        const previousPurchaseOrder = queryClient.getQueryData<PurchaseOrder>([
          'purchase-order',
          purchaseOrderId,
        ]);

        queryClient.setQueryData<{ purchaseOrder: PurchaseOrder } | undefined>(
          ['purchase-order', purchaseOrderId],
          (old) => {
            if (!old) return old;
            return {
              purchaseOrder: {
                ...old.purchaseOrder,
                stage: 'TOP',
                sub_stage: 'TOP: Shipped Samples',
              },
            };
          },
        );

        return { previousPurchaseOrder };
      },

      onError: (_, { purchaseOrderId }, context) => {
        if (context?.previousPurchaseOrder) {
          queryClient.setQueryData(
            ['purchase-order', purchaseOrderId],
            context.previousPurchaseOrder,
          );
        }
      },

      onSettled: async (_data, _error, { purchaseOrderId }) => {
        await queryClient.invalidateQueries([
          'purchase-order',
          purchaseOrderId,
        ]);
      },
    },
  );
};

export const useSetProp65 = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    SetProp65Params,
    { previousPurchaseOrder?: PurchaseOrder }
  >(
    ({ userId, poNumber, updates }) =>
      UserAPI.setProp65(userId, poNumber, updates),
    {
      onMutate: async ({ poNumber, updates }) => {
        await queryClient.cancelQueries(['purchase-order', poNumber]);

        const previousPurchaseOrder = queryClient.getQueryData<PurchaseOrder>([
          'purchase-order',
          poNumber,
        ]);

        queryClient.setQueryData<{ purchaseOrder: PurchaseOrder } | undefined>(
          ['purchase-order', poNumber],
          (old) => {
            if (!old) return old;

            const prop65Updates = updates.reduce(
              (acc, update) => {
                acc[update.lineId.toString()] = update.prop65;
                return acc;
              },
              {} as Record<string, boolean>,
            );

            const updatedLines = old.purchaseOrder.purchase_order_lines.map(
              (line) => {
                if (Object.hasOwn(prop65Updates, line.po_line_id)) {
                  return {
                    ...line,
                    prop65: prop65Updates[line.po_line_id],
                  };
                }
                return line;
              },
            );

            return {
              purchaseOrder: {
                ...old.purchaseOrder,
                purchase_order_lines: updatedLines,
              },
            };
          },
        );

        return { previousPurchaseOrder };
      },

      onError: (_error, { poNumber }, context) => {
        if (context?.previousPurchaseOrder) {
          queryClient.setQueryData(
            ['purchase-order', poNumber],
            context.previousPurchaseOrder,
          );
        }
      },

      onSettled: async (_data, _error, { poNumber }) => {
        await queryClient.invalidateQueries(['purchase-order', poNumber]);
      },
    },
  );
};
